import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Star, BadgeCheck, ArrowRight, Trophy, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RANK_MEDALS = ['🥇', '🥈', '🥉'];

/**
 * Compact top-5 agents leaderboard. Designed for /for-agents and landing
 * pages to create FOMO and reinforce agent value.
 */
const AgentLeaderboard = ({ country = 'UK', city, limit = 5 }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API_URL}/api/directory/agents`, { params: { country, city, limit } })
      .then(r => { if (!cancel) setAgents(r.data.items || []); })
      .catch(() => { if (!cancel) setAgents([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [country, city, limit]);

  if (loading) {
    return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#E07A3A] mx-auto" /></div>;
  }
  if (agents.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200" data-testid="agent-leaderboard">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-500" />
          <p className="font-semibold text-[#0F172A] text-sm">Top verified agents{city ? ` in ${city}` : ` · ${country}`}</p>
        </div>
        <Link to={`/find-a-professional?country=${country}${city ? `&city=${city}` : ''}&type=agent`} className="text-xs text-[#E07A3A] hover:underline font-semibold">
          View full directory →
        </Link>
      </div>
      <ul className="divide-y divide-slate-100" data-testid="agent-leaderboard-list">
        {agents.map((a, idx) => (
          <li key={a.id} className="flex items-center gap-3 p-4 hover:bg-[#F8FAFC] transition-colors" data-testid={`leaderboard-row-${idx}`}>
            <span className="w-8 text-center text-base font-bold text-slate-400">{RANK_MEDALS[idx] || `#${idx + 1}`}</span>
            <div className="w-10 h-10 bg-[#FEF3EC] border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0">
              {a.avatar ? <img src={a.avatar} alt={a.name} className="w-10 h-10 object-cover" /> :
                <span className="text-[#E07A3A] font-semibold text-sm">{(a.name || '?').slice(0, 2).toUpperCase()}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold text-[#0F172A] text-sm truncate">{a.name}</p>
                {a.verified_badge && <BadgeCheck className="h-3.5 w-3.5 text-[#E07A3A]" />}
              </div>
              <p className="text-[11px] text-slate-500 truncate">
                {a.city || a.country}
                {a.review_avg > 0 && (
                  <> · <Star className="h-2.5 w-2.5 inline text-amber-500 fill-amber-500" /> {a.review_avg.toFixed(1)} ({a.review_count})</>
                )}
                {a.listings_active > 0 && ` · ${a.listings_active} listings`}
              </p>
            </div>
            <Link to={`/agent/${a.id}`} className="text-xs text-[#E07A3A] hover:text-[#C4652E] font-semibold flex items-center gap-1">
              View <ArrowRight className="h-3 w-3" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AgentLeaderboard;
